import { calculatePrices } from "./calculatePrices";
import { TARGET_UTIL_OFFER_MARGIN } from "./constants";
import getCoefficientKeysByCategory from "./getCoefficientKeysByCategory";
import { getIndustryPercents } from "./getIndustyPercents";
import {
  Coefficients,
  DateRange,
  IndustryName,
  OfferItem,
  PriceCategory,
} from "./types";
import { countDaysInDateRange } from "./countDaysInDateRange";
import { countDaysInBothRanges } from "./countDaysInBothRanges";
import { getSurplusRange } from "./getSurplusRange";

const getFinalValue = (value: number | undefined) => {
  return value ? value / 100 : null;
};

export const calculateTargetUtilRate = (
  rentalRange: DateRange,
  avgRentalPeriod: number = 0,
  itemTargetUtilRate?: number,
  surplusRange?: DateRange,
) => {
  if (!itemTargetUtilRate) {
    return;
  }
  if (avgRentalPeriod === null) {
    avgRentalPeriod = 0;
  }

  let targetUtilResult;
  const targetUtilDecimal = Number(itemTargetUtilRate) / 100;
  // If target util rate is more than 70% then we need to increase it
  const multiplier = targetUtilDecimal > 0.7 ? 0.95 : 0.9;
  let targetUtilOfferCap = targetUtilDecimal + TARGET_UTIL_OFFER_MARGIN;
  let surplusDays = 0;
  if (targetUtilOfferCap > 1) {
    targetUtilOfferCap = 1;
  }

  const rentalDays = Math.floor(
    countDaysInDateRange(rentalRange.start, rentalRange.end),
  );

  surplusDays = surplusRange
    ? Math.max(
        Math.round(countDaysInBothRanges(rentalRange, surplusRange) - 7),
        0,
      )
    : 0;

  let discountDays =
    avgRentalPeriod === 0
      ? 0
      : rentalDays - avgRentalPeriod <= 0
        ? 0
        : rentalDays - avgRentalPeriod;

  discountDays =
    discountDays + surplusDays <= 0 ? 0 : discountDays + surplusDays;

  targetUtilResult =
    (multiplier * discountDays + targetUtilDecimal * (365 - discountDays)) /
    365;

  return targetUtilResult > targetUtilOfferCap
    ? targetUtilOfferCap
    : targetUtilResult;
};

export const calculateTargetUtilRateRamifleetOld = (
  targetUtilRate: number,
  rentalRange: DateRange,
) => {
  // It is agreed, that deprecation period is always 3 years for old ramifleet equipment
  const deprecationPeriod = 36;

  if (!rentalRange) {
    return 0;
  }

  const targetUtilRateDecimal = Number(targetUtilRate) / 100;

  const rentalDays = countDaysInDateRange(rentalRange.start, rentalRange.end);

  const deprecationInDays = deprecationPeriod * 30;

  const targetUtilResult =
    (rentalDays + targetUtilRateDecimal * (deprecationInDays - rentalDays)) /
    deprecationInDays;

  const percentageInteger = Math.round(targetUtilResult * 100);
  return percentageInteger > 100 ? 100 : percentageInteger;
};

export type RamiFleetParams = Pick<
  OfferItem,
  | "dayPriceCoefficient1"
  | "dayPriceCoefficient2"
  | "dayPriceCoefficient3"
  | "dayPriceCoefficient4"
  | "dayPriceCoefficient5"
  | "dayPriceCoefficient6"
  | "dayPriceCoefficient7"
  | "dayPriceCoefficientList"
  | "monthPriceCoefficient1"
  | "monthPriceCoefficient2"
  | "monthPriceCoefficient3"
  | "monthPriceCoefficient4"
  | "monthPriceCoefficient5"
  | "monthPriceCoefficient6"
  | "monthPriceCoefficient7"
  | "monthPriceCoefficientList"
  | "targetUtilRate"
  | "depreciationPeriod"
  | "rentalReadyPrice"
  | "itemIndustryPercents"
>;

export const countOfferPriceRamifleetOld = (
  item: RamiFleetParams,
  priceType: "DAY" | "MONTH",
  rentalRange: DateRange,
  priceCategoryCustomer: PriceCategory,
  industry: IndustryName,
) => {
  if (!item) {
    return 0;
  }
  let priceCategory;

  // Price category is 3 at maximum
  if (
    priceCategoryCustomer === PriceCategory.PriceCategory4 ||
    priceCategoryCustomer === PriceCategory.PriceCategory5 ||
    priceCategoryCustomer === PriceCategory.PriceCategory6 ||
    priceCategoryCustomer === PriceCategory.PriceCategory7
  ) {
    priceCategory = PriceCategory.PriceCategory3;
  } else {
    priceCategory = priceCategoryCustomer;
  }

  const coefficientKeys = getCoefficientKeysByCategory(
    priceCategory,
  ) as Coefficients;
  let dayCoefficient = getFinalValue(item[coefficientKeys.dayCoefficient]);
  let monthCoefficient = getFinalValue(item[coefficientKeys.monthCoefficient]);

  const { changeCoefficientDay, changeCoefficientMonth } = getIndustryPercents(
    item,
    industry,
    coefficientKeys,
  );

  // Modify coefficients according to industry percentages
  let finalDayCoefficient = dayCoefficient
    ? dayCoefficient * changeCoefficientDay
    : dayCoefficient;
  let finalMonthCoefficient = monthCoefficient
    ? monthCoefficient * changeCoefficientMonth
    : monthCoefficient;

  if (
    !finalMonthCoefficient ||
    !finalDayCoefficient ||
    !item.depreciationPeriod ||
    !rentalRange ||
    !item.targetUtilRate ||
    !item.rentalReadyPrice
  ) {
    return 0;
  }

  const targetUtilRateRamifleet = calculateTargetUtilRateRamifleetOld(
    item.targetUtilRate,
    rentalRange,
  );

  const prices = calculatePrices(
    Number(item.rentalReadyPrice),
    item.depreciationPeriod,
    targetUtilRateRamifleet / 100,
    finalDayCoefficient,
    finalMonthCoefficient,
  );

  return priceType === "DAY"
    ? prices.proposalDayPrice
    : prices.proposalMonthPrice;
};

export const calculateTargetUtilRateRamifleetNew = (
  targetUtilRate: number,
  depreciationPeriod: number,
  rentalRange: DateRange,
) => {
  if (!depreciationPeriod || !rentalRange) {
    return 0;
  }

  const targetUtilRateDecimal = Number(targetUtilRate) / 100;

  const rentalDays = countDaysInDateRange(rentalRange.start, rentalRange.end);

  const deprecationInDays = depreciationPeriod * 30;

  const targetUtilResult =
    (rentalDays + targetUtilRateDecimal * (deprecationInDays - rentalDays)) /
    deprecationInDays;

  const percentageInteger = Math.round(targetUtilResult * 100);
  return percentageInteger > 100 ? 100 : percentageInteger;
};

export const countOfferPriceRamifleetNew = (
  item: RamiFleetParams,
  priceType: "DAY" | "MONTH",
  rentalRange: DateRange,
  industry: IndustryName,
) => {
  if (!item) {
    return 0;
  }
  // Price category for new ramifleet is always "PriceCategory3 Month" and minimum value for coefficient is 3.0
  const coefficientKeys = getCoefficientKeysByCategory(
    PriceCategory.PriceCategory3,
  ) as Coefficients;
  let dayCoefficient = getFinalValue(item[coefficientKeys.dayCoefficient]);
  let monthCoefficient = getFinalValue(item[coefficientKeys.monthCoefficient]);

  const { changeCoefficientDay, changeCoefficientMonth } = getIndustryPercents(
    item,
    industry,
    coefficientKeys,
  );

  // Modify coefficients according to industry percentages
  let finalDayCoefficient = dayCoefficient
    ? dayCoefficient * changeCoefficientDay
    : dayCoefficient;
  let finalMonthCoefficient = monthCoefficient
    ? monthCoefficient * changeCoefficientMonth
    : monthCoefficient;

  if (
    !finalMonthCoefficient ||
    !finalDayCoefficient ||
    !item.depreciationPeriod ||
    !rentalRange ||
    !item.targetUtilRate ||
    !item.rentalReadyPrice
  ) {
    return 0;
  }

  if (finalMonthCoefficient < 3) {
    finalMonthCoefficient = 3;
  }

  const targetUtilRateRamifleet = calculateTargetUtilRateRamifleetNew(
    item.targetUtilRate,
    item.depreciationPeriod,
    rentalRange,
  );

  const prices = calculatePrices(
    Number(item.rentalReadyPrice),
    item.depreciationPeriod,
    targetUtilRateRamifleet / 100,
    finalDayCoefficient,
    finalMonthCoefficient,
  );

  return priceType === "DAY"
    ? prices.proposalDayPrice
    : prices.proposalMonthPrice;
};

export const countOfferPrice = (
  item: OfferItem,
  priceCategory: PriceCategory,
  priceType: "DAY" | "MONTH",
  rentalRange: DateRange,
  industry: IndustryName,
) => {
  const coefficientKeys = getCoefficientKeysByCategory(
    priceCategory,
  ) as Coefficients;

  const dayCoefficient = getFinalValue(item[coefficientKeys.dayCoefficient]);
  const monthCoefficient = getFinalValue(
    item[coefficientKeys.monthCoefficient],
  );

  const { changeCoefficientDay, changeCoefficientMonth } = getIndustryPercents(
    item,
    industry,
    coefficientKeys,
  );

  // Modify coefficients according to industry percentages
  const finalDayCoefficient = dayCoefficient
    ? dayCoefficient * changeCoefficientDay
    : dayCoefficient;

  const finalMonthCoefficient = monthCoefficient
    ? monthCoefficient * changeCoefficientMonth
    : monthCoefficient;

  const surplusRange = getSurplusRange(item, rentalRange);

  const targetUtilRate = calculateTargetUtilRate(
    rentalRange,
    item.avgRentalPeriod,
    item.targetUtilRate,
    surplusRange,
  );
  const rentalReadyPrice = Number(item.rentalReadyPrice);
  const depreciationPeriod = Number(item.depreciationPeriod);

  if (
    !finalDayCoefficient ||
    !finalMonthCoefficient ||
    !rentalReadyPrice ||
    !rentalReadyPrice ||
    !depreciationPeriod ||
    !targetUtilRate
  ) {
    return 0;
  }

  const prices = calculatePrices(
    rentalReadyPrice,
    depreciationPeriod,
    targetUtilRate,
    finalDayCoefficient,
    finalMonthCoefficient,
  );
  return priceType === "DAY"
    ? prices.proposalDayPrice
    : prices.proposalMonthPrice;
};
